/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import GatewayConnectionStore from '@app/stores/gateway/GatewayConnectionStore';
import MemberSidebarStore from '@app/stores/MemberSidebarStore';
import WindowStore from '@app/stores/WindowStore';
import {normalizeMemberListRanges} from '@app/utils/MemberListRangeUtils';
import {MEMBER_LIST_RANGE_MAX_SPAN} from '@fluxer/constants/src/GatewayConstants';
import throttle from 'lodash/throttle';
import {reaction} from 'mobx';
import {useCallback, useEffect, useRef} from 'react';

const UNFOCUS_UNSUBSCRIBE_DELAY_MS = 60000;
const INITIAL_MEMBER_RANGE: [number, number] = [0, MEMBER_LIST_RANGE_MAX_SPAN];
const THROTTLE_INTERVAL_MS = 200;
const RANGE_COOLDOWN_MS = 10000;
const COOLDOWN_PRUNE_INTERVAL_MS = 30000;

interface UseMemberListSubscriptionOptions {
	guildId: string;
	channelId: string;
	enabled: boolean;
	allowInitialUnfocusedLoad?: boolean;
}

interface UseMemberListSubscriptionResult {
	subscribe: (ranges: Array<[number, number]>) => void;
	unsubscribe: () => void;
}

function rangesEqual(left: Array<[number, number]>, right: Array<[number, number]>): boolean {
	if (left.length !== right.length) {
		return false;
	}

	for (let index = 0; index < left.length; index++) {
		const [leftStart, leftEnd] = left[index];
		const [rightStart, rightEnd] = right[index];
		if (leftStart !== rightStart || leftEnd !== rightEnd) {
			return false;
		}
	}

	return true;
}

function isSubsetOfRanges(inner: Array<[number, number]>, outer: Array<[number, number]>): boolean {
	if (inner.length === 0) {
		return true;
	}
	if (outer.length === 0) {
		return false;
	}

	for (const [innerStart, innerEnd] of inner) {
		let covered = false;
		for (const [outerStart, outerEnd] of outer) {
			if (outerStart <= innerStart && outerEnd >= innerEnd) {
				covered = true;
				break;
			}
		}
		if (!covered) {
			return false;
		}
	}

	return true;
}

function subtractRanges(ranges: Array<[number, number]>, subtract: Array<[number, number]>): Array<[number, number]> {
	if (subtract.length === 0) {
		return ranges;
	}

	let result = [...ranges];
	for (const [subStart, subEnd] of subtract) {
		const next: Array<[number, number]> = [];
		for (const [rStart, rEnd] of result) {
			if (rEnd < subStart || rStart > subEnd) {
				next.push([rStart, rEnd]);
			} else {
				if (rStart < subStart) {
					next.push([rStart, subStart - 1]);
				}
				if (rEnd > subEnd) {
					next.push([subEnd + 1, rEnd]);
				}
			}
		}
		result = next;
	}

	return result;
}

function rangeKey(start: number, end: number): string {
	return `${start}:${end}`;
}

function getCooldownCoveredRanges(cooldownMap: Map<string, number>, now: number): Array<[number, number]> {
	const covered: Array<[number, number]> = [];
	for (const [key, timestamp] of cooldownMap) {
		if (now - timestamp < RANGE_COOLDOWN_MS) {
			const parts = key.split(':');
			covered.push([Number(parts[0]), Number(parts[1])]);
		}
	}
	return normalizeMemberListRanges(covered);
}

function pruneCooldownMap(cooldownMap: Map<string, number>, now: number): void {
	for (const [key, timestamp] of cooldownMap) {
		if (now - timestamp >= RANGE_COOLDOWN_MS) {
			cooldownMap.delete(key);
		}
	}
}

export function useMemberListSubscription({
	guildId,
	channelId,
	enabled,
	allowInitialUnfocusedLoad = false,
}: UseMemberListSubscriptionOptions): UseMemberListSubscriptionResult {
	const lastRangesRef = useRef<Array<[number, number]>>([INITIAL_MEMBER_RANGE]);
	const pendingRangesRef = useRef<Array<[number, number]> | null>(null);
	const unfocusTimeoutRef = useRef<number | null>(null);
	const isSubscribedRef = useRef(false);
	const lastSubscribedRangesRef = useRef<Array<[number, number]>>([]);
	const lastSessionVersionRef = useRef(MemberSidebarStore.sessionVersion);
	const lastGatewayReadyRef = useRef(GatewayConnectionStore.isReady);
	const hadChannelListRef = useRef(MemberSidebarStore.getList(guildId, channelId) !== undefined);
	const initialUnfocusedLoadAttemptedRef = useRef(false);
	const cooldownMapRef = useRef(new Map<string, number>());
	const cooldownPruneTimerRef = useRef<number | null>(null);

	const clearUnfocusTimeout = useCallback(() => {
		if (unfocusTimeoutRef.current !== null) {
			window.clearTimeout(unfocusTimeoutRef.current);
			unfocusTimeoutRef.current = null;
		}
	}, []);

	const attemptSubscribe = useCallback(
		(ranges: Array<[number, number]>, bypassCooldown = false, forceSubscriptionUpdate = false) => {
			if (!enabled) {
				return;
			}

			const windowFocused = WindowStore.focused;
			const allowUnfocusedInitialLoad = allowInitialUnfocusedLoad && !initialUnfocusedLoadAttemptedRef.current;
			const allowUnfocusedResubscribe = !isSubscribedRef.current;
			if (!windowFocused && !allowUnfocusedInitialLoad && !allowUnfocusedResubscribe) {
				return;
			}

			if (!windowFocused && allowUnfocusedInitialLoad) {
				initialUnfocusedLoadAttemptedRef.current = true;
			}

			if (!forceSubscriptionUpdate && isSubscribedRef.current && rangesEqual(lastSubscribedRangesRef.current, ranges)) {
				return;
			}

			if (
				!forceSubscriptionUpdate &&
				isSubscribedRef.current &&
				isSubsetOfRanges(ranges, lastSubscribedRangesRef.current)
			) {
				return;
			}

			let rangesToSend = ranges;
			if (!bypassCooldown && ranges.length > 0) {
				const now = Date.now();
				const cooldownCovered = getCooldownCoveredRanges(cooldownMapRef.current, now);
				const uncovered = subtractRanges(ranges, cooldownCovered);
				if (uncovered.length === 0 && isSubscribedRef.current) {
					return;
				}
				rangesToSend = ranges;
			}

			const now = Date.now();
			for (const [start, end] of rangesToSend) {
				cooldownMapRef.current.set(rangeKey(start, end), now);
			}

			const shouldForceGuildSync = forceSubscriptionUpdate || !isSubscribedRef.current;
			GatewayConnectionStore.syncGuildIfNeeded(guildId, 'member-list-subscribe', shouldForceGuildSync);
			MemberSidebarStore.subscribeToChannel(guildId, channelId, rangesToSend, forceSubscriptionUpdate);
			isSubscribedRef.current = true;
			lastSubscribedRangesRef.current = rangesToSend;
		},
		[guildId, channelId, enabled, allowInitialUnfocusedLoad],
	);

	const throttledFlushRef = useRef(
		throttle(
			() => {
				const pendingRanges = pendingRangesRef.current;
				if (!pendingRanges) {
					return;
				}
				pendingRangesRef.current = null;
				attemptSubscribe(pendingRanges);
			},
			THROTTLE_INTERVAL_MS,
			{leading: false, trailing: true},
		),
	);

	useEffect(() => {
		throttledFlushRef.current = throttle(
			() => {
				const pendingRanges = pendingRangesRef.current;
				if (!pendingRanges) {
					return;
				}
				pendingRangesRef.current = null;
				attemptSubscribe(pendingRanges);
			},
			THROTTLE_INTERVAL_MS,
			{leading: false, trailing: true},
		);

		return () => {
			throttledFlushRef.current.cancel();
		};
	}, [attemptSubscribe]);

	const queueSubscribe = useCallback((ranges: Array<[number, number]>) => {
		const normalizedRanges = normalizeMemberListRanges(ranges);
		lastRangesRef.current = normalizedRanges;
		pendingRangesRef.current = normalizedRanges;
		throttledFlushRef.current();
	}, []);

	const subscribe = useCallback(
		(ranges: Array<[number, number]>) => {
			queueSubscribe(ranges);
		},
		[queueSubscribe],
	);

	const unsubscribe = useCallback(() => {
		clearUnfocusTimeout();
		throttledFlushRef.current.cancel();
		pendingRangesRef.current = null;
		if (isSubscribedRef.current) {
			MemberSidebarStore.unsubscribeFromChannel(guildId, channelId);
			isSubscribedRef.current = false;
		}
		lastSubscribedRangesRef.current = [];
	}, [guildId, channelId, clearUnfocusTimeout]);

	const resubscribe = useCallback(() => {
		if (lastRangesRef.current.length > 0) {
			attemptSubscribe(lastRangesRef.current, true);
		}
	}, [attemptSubscribe]);

	useEffect(() => {
		lastRangesRef.current = [INITIAL_MEMBER_RANGE];
		pendingRangesRef.current = null;
		isSubscribedRef.current = false;
		lastSubscribedRangesRef.current = [];
		lastSessionVersionRef.current = MemberSidebarStore.sessionVersion;
		lastGatewayReadyRef.current = GatewayConnectionStore.isReady;
		hadChannelListRef.current = MemberSidebarStore.getList(guildId, channelId) !== undefined;
		initialUnfocusedLoadAttemptedRef.current = false;
		cooldownMapRef.current.clear();
		clearUnfocusTimeout();
		throttledFlushRef.current.cancel();
	}, [guildId, channelId, clearUnfocusTimeout]);

	useEffect(() => {
		const pruneTimer = window.setInterval(() => {
			pruneCooldownMap(cooldownMapRef.current, Date.now());
		}, COOLDOWN_PRUNE_INTERVAL_MS);
		cooldownPruneTimerRef.current = pruneTimer;

		return () => {
			window.clearInterval(pruneTimer);
			cooldownPruneTimerRef.current = null;
		};
	}, []);

	useEffect(() => {
		if (!enabled) {
			unsubscribe();
			return;
		}

		resubscribe();

		const disposeFocusReaction = reaction(
			() => WindowStore.focused,
			(focused) => {
				if (focused) {
					clearUnfocusTimeout();
					resubscribe();
					return;
				}

				clearUnfocusTimeout();
				unfocusTimeoutRef.current = window.setTimeout(() => {
					unfocusTimeoutRef.current = null;
					if (!WindowStore.focused && isSubscribedRef.current) {
						MemberSidebarStore.unsubscribeFromChannel(guildId, channelId);
						isSubscribedRef.current = false;
						lastSubscribedRangesRef.current = [];
					}
				}, UNFOCUS_UNSUBSCRIBE_DELAY_MS);
			},
		);

		const disposeSessionReaction = reaction(
			() => MemberSidebarStore.sessionVersion,
			(newVersion) => {
				if (newVersion !== lastSessionVersionRef.current) {
					lastSessionVersionRef.current = newVersion;
					isSubscribedRef.current = false;
					lastSubscribedRangesRef.current = [];
					cooldownMapRef.current.clear();
					if (WindowStore.focused) {
						resubscribe();
					}
				}
			},
		);

		const disposeGatewayReadyReaction = reaction(
			() => GatewayConnectionStore.isReady,
			(isReady) => {
				const wasReady = lastGatewayReadyRef.current;
				lastGatewayReadyRef.current = isReady;

				if (!enabled) {
					return;
				}

				if (isReady && !wasReady) {
					attemptSubscribe(lastRangesRef.current, true, true);
				}
			},
		);

		const disposeGuildListReaction = reaction(
			() => MemberSidebarStore.getList(guildId, channelId) !== undefined,
			(hasChannelList) => {
				const hadChannelList = hadChannelListRef.current;
				hadChannelListRef.current = hasChannelList;

				if (hadChannelList && !hasChannelList) {
					isSubscribedRef.current = false;
					lastSubscribedRangesRef.current = [];
					cooldownMapRef.current.clear();
				}

				if (!hasChannelList && enabled) {
					resubscribe();
				}
			},
		);

		return () => {
			disposeFocusReaction();
			disposeSessionReaction();
			disposeGatewayReadyReaction();
			disposeGuildListReaction();
			clearUnfocusTimeout();
			throttledFlushRef.current.cancel();
			unsubscribe();
		};
	}, [guildId, channelId, enabled, resubscribe, unsubscribe, clearUnfocusTimeout, attemptSubscribe]);

	useEffect(() => {
		if (enabled && !isSubscribedRef.current) {
			queueSubscribe(lastRangesRef.current);
		}
	}, [enabled, queueSubscribe]);

	return {subscribe, unsubscribe};
}
